/**
 * @flow weak
 */

import { createSelector, createStructuredSelector } from 'reselect';
import { getUser, getUserEmail, getAmplitudeInstance } from '../session/getters';
import { REGULAR, TIMED_ENTRY } from '../../constants';
import {
  showCancelOrder,
  showTicketTransfer,
  showCancelTicketTransfer,
  showTicketTransferDisabled,
  showResendTicketTransfer,
  showRefund,
  showChangeDate,
  showEditGuestInfo,
  showEditBuyerInfo,
  showBookingProtect,
} from '../modal/getters';

// Raw Selector

// Tickets
export const allCurrentTicketData = (state) => state.myTickets.tickets.current;
export const allPastTicketData = (state) => state.myTickets.tickets.past;
export const listings = (state) => state.myTickets.tickets.listings;
export const ticketsError = (state) => state.myTickets.tickets.ticketsError;

// CostItems
export const costItems = (state) => state.myTickets.costItems;

// Passes
export const myPasses = (state) => state.myTickets.passes;

// UI
export const guestHostFields = (state) => state.myTickets.ui.guestHostFields;
export const buyerHostFields = (state) => state.myTickets.ui.buyerHostFields;
export const currentHost = (state) => state.myTickets.ui.selectedHostId;
export const refundMessage = (state) => state.myTickets.ui.refundMessage;
export const selectedTicketId = (state) => state.myTickets.ui.selectedTicketId;
export const selectedCostItem = (state) => state.myTickets.ui.selectedCostItem;
export const ticketTransferInputValues = (state) => state.myTickets.ui.inputFields;
export const loadingPast = (state) => state.myTickets.ui.loadingPast;
export const ticketLoadIndices = (state) => state.myTickets.ui.ticketLoadIndices;
export const loadingCurrent = (state) => state.myTickets.ui.loadingCurrent;
export const loadingPasses = (state) => state.myTickets.ui.loadingPasses;
export const flash = (state) => state.myTickets.ui.flash;
export const banner = (state) => state.myTickets.ui.banner;
export const requestPending = (state) => state.myTickets.ui.requestPending;
export const requestSuccess = (state) => state.myTickets.ui.requestSuccess;
export const refundPageLink = (state) => state.myTickets.ui.refundPageLink;
export const intermediateLoading = (state) => state.myTickets.ui.intermediateLoading;

// Pagination
export const pastPagination = (state) => state.myTickets.pagination.pastPagination;
export const currentPagination = (state) => state.myTickets.pagination.currentPagination;
export const loadingPagination = (state) => state.myTickets.pagination.loadingPagination;
export const pastPage = (state) => state.myTickets.pagination.pastPage;
export const currentPage = (state) => state.myTickets.pagination.currentPage;

// Selectors
export const currentTickets = createSelector(
  [allCurrentTicketData, listings, getUserEmail],
  (allCurrentTicketData, listings, userEmail) => {
    if (allCurrentTicketData && allCurrentTicketData.tickets) {
      const findTicketInstallments = (installments, installmentIds) => {
        return installments.filter(installment => installmentIds.includes(installment.id));
      };

      let installmentIds = [];
      let installments = [];

      return allCurrentTicketData.tickets.map(ticket => {
        const listing = listings[ticket.listing_id];
        const paymentPlan = ticket.installment_ids.length > 0;
        const host = allCurrentTicketData.users.find(user => user.id === listing.host_id);

        if (paymentPlan) {
          installmentIds = ticket.installment_ids;
          if (installmentIds.length > 0) {
            installments = findTicketInstallments(allCurrentTicketData.installments, installmentIds);
          }
        }

        ticket.event_type = listing.timed_entry ? TIMED_ENTRY : REGULAR;

        return {
          ticket,
          noneditable_offset: listing.noneditable_offset,
          title: listing.title,
          hideDate: listing.hide_date,
          location: listing.venue_name || listing.address,
          hostId: listing.host_id,
          host: host.first_name,
          eventImage: listing.image_url_160,
          startTime: ticket.start_time,
          endTime: ticket.end_time,
          slugParam: listing.slug_param,
          canGenerateInvoice: listing.can_generate_invoice,
          paymentPlan: {
            installments: paymentPlan ? installments : false
          },
          isSecondaryBuyer: ticket.buyer_email !== userEmail,
          dateDisplayOption: listing.date_display_option,
        };
      }).sort((a, b) => {
        return new Date(a.startTime) > new Date(b.startTime);
      });
    }
    return null;
  }
);

export const pastTickets = createSelector(
  [allPastTicketData, listings, getUserEmail],
  (allPastTicketData, listings, userEmail) => {
    if (allPastTicketData && allPastTicketData.tickets) {
      const findTicketInstallments = (installments, installmentIds) => {
        return installments.filter(installment => installmentIds.includes(installment.id));
      };

      let installmentIds = [];
      let installments = [];

      return allPastTicketData.tickets.map(ticket => {
        const listing = listings[ticket.listing_id];

        const paymentPlan = ticket.installment_ids.length > 0;

        if (paymentPlan) {
          installmentIds = ticket.installment_ids;
          if (installmentIds.length > 0) {
            installments = findTicketInstallments(allPastTicketData.installments, installmentIds);
          }
        }
        return {
          ticket,
          title: listing.title,
          hideDate: listing.hide_date,
          location: listing.venue_name || listing.address,
          hostId: listing.host_id,
          eventImage: listing.image_url_160,
          startTime: ticket.start_time,
          endTime: ticket.end_time,
          slugParam: listing.slug_param,
          canGenerateInvoice: listing.can_generate_invoice,
          paymentPlan: {
            installments: paymentPlan ? installments : false
          },
          isSecondaryBuyer: ticket.buyer_email !== userEmail,
          dateDisplayOption: listing.date_display_option,
        };
      }).sort((a, b) => {
        return new Date(a.startTime) > new Date(b.startTime);
      });
    }
    return null;
  }
);

export const selectedTicket = createSelector(
  [allCurrentTicketData, selectedTicketId],
  (allCurrentTicketData, selectedTicketId) => {
    if (allCurrentTicketData && selectedTicketId) {
      return allCurrentTicketData.tickets.find(ticket => ticket.id === selectedTicketId);
    }
    return null;
  }
);

export const costItemsForSelectedTicket = createSelector(
  [currentTickets, selectedTicketId],
  (currentTickets, selectedTicketId) => {
    if (currentTickets && selectedTicketId) {
      const ticket = currentTickets.find(data => data.ticket.id === selectedTicketId).ticket;
      return ticket ? ticket.cost_item_ids : null;
    }
    return null;
  }
);

export const listingForSelectedCostItem = createSelector(
  [costItems, selectedCostItem],
  (costItems, selectedCostItem) => {
    if (costItems && costItems.length > 0 && selectedCostItem) {
      return costItems.find(costItem => costItem.cost_item.id === selectedCostItem).listings[0];
    }
    return null;
  },
);

export const selectedTransferId = createSelector(
  [costItems, selectedCostItem],
  (costItems, selectedCostItem) => {
    if (costItems && costItems.length > 0 && selectedCostItem) {
      const selectedTransfers = costItems
        .find(costItem => costItem.cost_item.id === selectedCostItem)
        .transfers;

      if (selectedTransfers.length > 0) {
        return selectedTransfers
          .find(transfer => transfer.cost_item_id === selectedCostItem)
          .id;
      }
    }
    return null;
  }
);

export const loadingMyTickets = createSelector(
  [loadingCurrent, loadingPast],
  (current, past) => {
    if (current || past) {
      return true;
    }
    return false;
  }
);

export const noEvents = createSelector(
  [currentTickets, pastTickets],
  (currentTickets, pastTickets) => {
    if (currentTickets || pastTickets) {
      if (currentTickets) {
        return !currentTickets.length > 0;
      }
      if (pastTickets) {
        return !pastTickets.length > 0;
      }
    }
    return true;
  }
);

export const loadingMyPasses = createSelector(
  [loadingPasses],
  (loadingPasses) => {
    if (loadingPasses) {
      return true;
    }
    return false;
  }
);


export const noPasses = createSelector(
  [myPasses],
  (myPasses) => {
    if (myPasses && myPasses.passes) {
      return !myPasses.passes.length > 0;
    }
    return true;
  }
);

export const openedPassId = createSelector(
  [myPasses],
  (myPasses) => {
    if (myPasses && myPasses.passes && myPasses.passes.length > 0) {
      const openedPass = myPasses.passes.find(pass => pass.expanded);
      if (openedPass) {
        return openedPass.id;
      }
    }
    return null;
  }
);

export const getOpenedPass = createSelector(
  [myPasses],
  (myPasses) => {
    if (myPasses && myPasses.passes && myPasses.passes.length > 0) {
      return myPasses.passes.find(pass => pass.expanded);
    }

    return null;
  }
);

export const getListingsForOpenedPass = createSelector(
  [myPasses, openedPassId],
  (myPasses, passId) => {
    if (myPasses && myPasses.passes && myPasses.passes.length > 0 && passId) {
      const pass = myPasses.passes.find(pass => pass.id === passId);
      return pass.listingsForThisPass;
    }

    return null;
  }
);

export const listingsForOpenedPass = createSelector(
  [getListingsForOpenedPass],
  (listingsForOpenedPass) => {
    if (listingsForOpenedPass && listingsForOpenedPass.listings) {
      // 1 - create normalized events for easy lookup
      const normalizedEvents = {};
      if (listingsForOpenedPass.events) {
        for (let i = 0; i < listingsForOpenedPass.events.length; i++) {
          normalizedEvents[listingsForOpenedPass.events[i].id] = listingsForOpenedPass.events[i];
        }
      }

      // 2 - normalize capacities by event_id
      const normalizedCapacities = {};
      if (listingsForOpenedPass.capacities) {
        for (let i = 0; i < listingsForOpenedPass.capacities.length; i++) {
          normalizedCapacities[listingsForOpenedPass.capacities[i].event_id] = listingsForOpenedPass.capacities[i];
        }
      }

      // helper func to filter bad listings
      const validListings = listing => {
        // not sold out
        if (normalizedCapacities[listing.event_id] && normalizedCapacities[listing.event_id].count_available === 0) {
          return false;
        }

        return listing.state === 'posted' && listing.rate_type === 'ticket';
      };

      return listingsForOpenedPass.listings
        .filter(validListings) // remove bad listings
        .map(listing => { // attach event_id to the listing, and all rates
          listing.event = normalizedEvents[listing.event_id];
          listing.availableRates = listingsForOpenedPass.rates
            .filter(rate => rate.listing_id === listing.id)
            .map(rate => rate.name)
            .join(', ');
          return listing;
        })
        .sort((a, b) => a.event.start_stamp - b.event.start_stamp);
    }

    return [];
  }
);

export const costItemForSelectedCostItemId = createSelector(
  [costItems, selectedCostItem],
  (costItems, selectedCostItem) => {
    if (costItems && selectedCostItem) {
      return costItems.find(costItem => costItem.cost_item.id === selectedCostItem);
    }
    return null;
  }
);

export const costItemsForSelectedTicketId = createSelector(
  [selectedTicket, costItems],
  (ticket, costItems) => {
    if (ticket && costItems) {
      return costItems.filter(costItem => ticket.cost_item_ids.includes(costItem.cost_item.id));
    }
    return null;
  }
);

export const addOnCostItemsForSelectedTicketId = createSelector(
  [costItemsForSelectedTicketId],
  (costItems) => {
    if (costItems) {
      return costItems.filter(costItem => costItem.cost_item.rate_type === 'AddOnRate');
    }
    return null;
  }
);

export const currentTicketHostFields = createSelector(
  [allCurrentTicketData, selectedTicketId],
  (allCurrentTicketData, selectedTicketId) => {
    if (allCurrentTicketData && selectedTicketId) {
      const selectedTicket = allCurrentTicketData.tickets.find(ticket => ticket.id === selectedTicketId);
      if (selectedTicket.host_field_ids && selectedTicket.host_field_ids.length > 0) {
        return allCurrentTicketData.host_fields.filter(hf => {
          return selectedTicket.host_field_ids.includes(hf.id);
        });
      }
    }
    return null;
  }
);

export const currentOrdersPagination = createSelector(
  [allCurrentTicketData],
  (allCurrentTicketData) => {
    if (allCurrentTicketData) {
      return allCurrentTicketData.meta;
    }
    return null;
  }
);

// connector
export default createStructuredSelector({
  currentTickets,
  pastTickets,
  ticketLoadIndices,
  costItems,
  myPasses,
  currentHost,
  refundMessage,
  selectedTicketId,
  selectedTicket,
  costItemsForSelectedTicketId,
  addOnCostItemsForSelectedTicketId,
  costItemsForSelectedTicket,
  selectedCostItem,
  selectedTransferId,
  listingForSelectedCostItem,
  ticketTransferInputValues,
  guestHostFields,
  buyerHostFields,
  costItemForSelectedCostItemId,
  currentTicketHostFields,
  currentOrdersPagination,
  currentPagination,
  pastPagination,
  loadingPagination,
  pastPage,
  currentPage,
  loadingMyTickets,
  loadingMyPasses,
  noEvents,
  noPasses,
  flash,
  banner,
  requestPending,
  requestSuccess,
  user: getUser,
  amplitudeInstance: getAmplitudeInstance,
  showCancelOrder,
  showTicketTransfer,
  showCancelTicketTransfer,
  showTicketTransferDisabled,
  showResendTicketTransfer,
  showRefund,
  showChangeDate,
  showEditGuestInfo,
  showEditBuyerInfo,
  listingsForOpenedPass,
  refundPageLink,
  intermediateLoading,
  ticketsError,
  showBookingProtect
});



// WEBPACK FOOTER //
// ./src/modules/my-tickets/getters.js